import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../../core/toast.service';

@Component({
  selector: 'app-notification-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Unified Notification System (Top Right) -->
    <div
      class="fixed top-4 right-4 space-y-3 pointer-events-none max-w-sm z-[99999] sm:max-w-md"
    >
      @for (toast of toasts; track toast.id) {
      <div
        class="w-full bg-white/95 shadow-xl rounded-xl pointer-events-auto ring-1 ring-gray-200/50 overflow-hidden transform transition-all duration-300 ease-out hover:shadow-2xl hover:scale-[1.02]"
        [class]="getToastClasses(toast.type)"
      >
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center"
                [class]="getIconClasses(toast.type)"
              >
                @switch (toast.type) { @case ('success') {
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                } @case ('error') {
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                } @case ('warning') {
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                } @default {
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                } }
              </div>
            </div>
            <div class="ml-3 flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900">
                {{ toast.title }}
              </p>
              <p class="mt-1 text-sm text-gray-600 leading-relaxed">
                {{ toast.message }}
              </p>
            </div>
            <div class="ml-4 flex-shrink-0">
              <button
                type="button"
                class="inline-flex rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-gray-400 transition-all duration-200"
                (click)="closeToast(toast.id)"
                title="Fechar notificação"
              >
                <span class="sr-only">Fechar</span>
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <!-- Progress bar for auto-dismiss -->
        @if (!toast.persistent && toast.duration) {
        <div class="h-1 bg-gray-100">
          <div
            class="h-full transition-all ease-linear"
            [class]="getProgressClasses(toast.type)"
            [style.animation]="'progress ' + toast.duration + 'ms linear'"
          ></div>
        </div>
        }
      </div>
      }
    </div>
  `,
  styles: [
    `
      @keyframes progress {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }

      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }

      .toast-enter {
        animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      }

      .toast-exit {
        animation: slideOutRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .animate-slide-in-right {
        animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      }

      /* Mobile responsiveness */
      @media (max-width: 640px) {
        .toast-container {
          left: 1rem;
          right: 1rem;
          top: 1rem;
          max-width: none;
        }
      }
    `,
  ],
})
export class NotificationContainerComponent implements OnInit, OnDestroy {
  protected toastService = inject(ToastService);

  ngOnInit() {
    console.log('🔔 NotificationContainerComponent initialized');
  }

  ngOnDestroy() {
    console.log('🔔 NotificationContainerComponent destroyed');
  }

  // Getter for reactive data
  get toasts() {
    return this.toastService.toasts$();
  }

  // Toast methods
  getToastClasses(type: string): string {
    const baseClasses = 'animate-slide-in-right backdrop-blur-sm';
    switch (type) {
      case 'success':
        return `${baseClasses} border-l-4 border-green-500 bg-green-50/90`;
      case 'error':
        return `${baseClasses} border-l-4 border-red-500 bg-red-50/90`;
      case 'warning':
        return `${baseClasses} border-l-4 border-yellow-500 bg-yellow-50/90`;
      default:
        return `${baseClasses} border-l-4 border-blue-500 bg-blue-50/90`;
    }
  }

  getIconClasses(type: string): string {
    switch (type) {
      case 'success':
        return 'bg-green-100 text-green-700 ring-2 ring-green-200';
      case 'error':
        return 'bg-red-100 text-red-700 ring-2 ring-red-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-200';
      default:
        return 'bg-blue-100 text-blue-700 ring-2 ring-blue-200';
    }
  }

  getProgressClasses(type: string): string {
    switch (type) {
      case 'success':
        return 'bg-gradient-to-r from-green-500 to-green-600';
      case 'error':
        return 'bg-gradient-to-r from-red-500 to-red-600';
      case 'warning':
        return 'bg-gradient-to-r from-yellow-500 to-yellow-600';
      default:
        return 'bg-gradient-to-r from-blue-500 to-blue-600';
    }
  }

  closeToast(id: string): void {
    this.toastService.removeToast(id);
  }
}
